import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Trash2,
  ArrowRight,
  Download,
  Plus,
  PenLine,
} from 'lucide-react';
import { useCpPortal } from '../../hooks/useCpPortal';

const WALLET_RAIL = 'wallet';

interface BeneficiaryValidation {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

interface CensusRow {
  bucket: string;
  msisdn: string | null;
  typo?: { mistypeProbability?: number; suggestedMatch?: string | null } | null;
}

interface ParsedBeneficiary {
  beneficiaryName: string;
  msisdn?: string | null;
  email?: string | null;
  amount: number;
  reference: string | null;
  employeeRef: string | null;
  paymentBatchReference?: string | null;
  validation: BeneficiaryValidation;
  census?: CensusRow | null;
}

interface UploadSummary {
  total: number;
  valid: number;
  invalid: number;
  totalAmount: number;
}

interface CensusCounts {
  registered: number;
  invite_required: number;
  probable_typo: number;
  invalid: number;
}

const formatZAR = (n: number) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 2 }).format(n);

interface ManualRow {
  employeeRef: string;
  name: string;
  msisdn: string;
  email: string;
  amount: string;
  reference: string;
}

const BUCKET_LABELS: Record<string, string> = {
  registered: 'Wallet ready',
  invite_required: 'Invite required',
  probable_typo: 'Probable typo',
  invalid: 'Invalid',
};

/** Canonical CSV/XLS columns — manual entry uses the same names. */
const WALLET_UPLOAD_FIELDS = [
  {
    manualKey: 'employeeRef' as keyof ManualRow,
    csvHeader: 'your_reference',
    required: false,
    hint: 'Optional internal rep or payroll code',
    placeholder: 'REP001',
    mono: false,
  },
  {
    manualKey: 'name' as keyof ManualRow,
    csvHeader: 'recipient_name',
    required: true,
    hint: 'Beneficiary full name',
    placeholder: 'Jane Smith',
    mono: false,
  },
  {
    manualKey: 'msisdn' as keyof ManualRow,
    csvHeader: 'msisdn',
    required: true,
    hint: 'Recipient mobile in international format 27XXXXXXXXX — this is the wallet lookup key (not the payment reference). Local 082… is auto-converted.',
    placeholder: '27821234567',
    mono: true,
  },
  {
    manualKey: 'email' as keyof ManualRow,
    csvHeader: 'email',
    required: false,
    hint: 'Optional; recommended for invite SMS and confirmation email',
    placeholder: 'jane@example.com',
    mono: false,
  },
  {
    manualKey: 'amount' as keyof ManualRow,
    csvHeader: 'amount_zar',
    required: true,
    hint: 'Payment amount in ZAR',
    placeholder: '500.00',
    mono: true,
  },
  {
    manualKey: 'reference' as keyof ManualRow,
    csvHeader: 'reference',
    required: false,
    hint: 'Optional payment description on the beneficiary wallet statement',
    placeholder: 'Commission Mar',
    mono: false,
  },
] as const;

const DEPOSIT_BATCH_FIELD = {
  csvHeader: 'payment_batch_reference',
  required: true,
  hint: 'Same value on every row. Quote this exact reference on your bulk EFT to MyMoolah for this batch.',
  placeholder: 'PAY-PARTNER-20260701-001',
} as const;

function CsvColumnLabel({ name, required }: { name: string; required?: boolean }) {
  return (
    <span className="font-mono text-[11px] font-medium text-[var(--foreground)]">
      {name}
      {required ? <span className="text-red-600"> *</span> : null}
    </span>
  );
}

type Step = 'upload' | 'preview' | 'configure';
type UploadMode = 'file' | 'manual';

const EMPTY_MANUAL_ROW: ManualRow = {
  employeeRef: '',
  name: '',
  msisdn: '',
  email: '',
  amount: '',
  reference: '',
};

interface PreviewPayload {
  beneficiaries: ParsedBeneficiary[];
  summary: UploadSummary;
  census: CensusCounts;
  filename?: string | null;
}

export const ClientUploadOverlay: React.FC = () => {
  const [step, setStep] = useState<Step>('upload');
  const [uploadMode, setUploadMode] = useState<UploadMode>('file');
  const [file, setFile] = useState<File | null>(null);
  const [manualRows, setManualRows] = useState<ManualRow[]>([{ ...EMPTY_MANUAL_ROW }]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [beneficiaries, setBeneficiaries] = useState<ParsedBeneficiary[]>([]);
  const [summary, setSummary] = useState<UploadSummary | null>(null);
  const [census, setCensus] = useState<CensusCounts | null>(null);
  const [paymentBatchRef, setPaymentBatchRef] = useState('');
  const [fundingMode, setFundingMode] = useState<'prefunded_float' | 'deposit_per_batch'>('prefunded_float');
  const [availableCents, setAvailableCents] = useState<number | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { apiBase, authHeaders, cpPath } = useCpPortal();
  const navigate = useNavigate();

  const isDepositPerBatch = fundingMode === 'deposit_per_batch';
  const requiredCents = summary ? Math.round(Number(summary.totalAmount) * 100) : 0;
  const floatShortfall = !isDepositPerBatch
    && availableCents != null
    && requiredCents > 0
    && availableCents < requiredCents;

  useEffect(() => {
    fetch(`${apiBase}/prefunding`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data) => {
        if (data.success && data.data) {
          setFundingMode(data.data.disbursementFundingMode || 'prefunded_float');
          setAvailableCents(data.data.availableCents ?? null);
        }
      })
      .catch(() => {});
  }, [apiBase, authHeaders]);

  const handleFileSelect = (f: File) => {
    const validExts = ['.csv', '.xlsx', '.xls'];
    const ext = '.' + f.name.split('.').pop()?.toLowerCase();
    if (!validExts.includes(ext)) {
      setError('Unsupported file type. Use CSV or Excel (.xlsx).');
      return;
    }
    setFile(f);
    setError('');
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFileSelect(f);
  }, []);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const applyPreviewResponse = (data: PreviewPayload) => {
    const rows = data.beneficiaries || [];
    setBeneficiaries(rows);
    setSummary(data.summary || null);
    setCensus(data.census || null);
    const fromCsv = rows.find((b) => b.paymentBatchReference)?.paymentBatchReference || '';
    setPaymentBatchRef(fromCsv);
    setStep('preview');
  };

  const updateManualRow = (index: number, field: keyof ManualRow, value: string) => {
    setManualRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addManualRow = () => setManualRows((prev) => [...prev, { ...EMPTY_MANUAL_ROW }]);

  const removeManualRow = (index: number) => {
    setManualRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const validateManualRowsClient = (): string | null => {
    const filled = manualRows.filter(
      (r) => r.name.trim() || r.msisdn.trim() || r.amount.trim(),
    );
    if (filled.length === 0) return 'Add at least one beneficiary';
    for (let i = 0; i < filled.length; i++) {
      const r = filled[i];
      if (!r.name.trim()) return `Row ${i + 1}: recipient name is required`;
      if (!r.msisdn.trim()) return `Row ${i + 1}: mobile number is required`;
      const amount = parseFloat(r.amount);
      if (Number.isNaN(amount) || amount <= 0) return `Row ${i + 1}: enter a valid amount greater than R 0.00`;
    }
    return null;
  };

  const handleManualPreview = async () => {
    const validationError = validateManualRowsClient();
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploading(true);
    setError('');

    const payload = manualRows
      .filter((r) => r.name.trim() || r.msisdn.trim() || r.amount.trim())
      .map((r) => ({
        name: r.name.trim(),
        msisdn: r.msisdn.trim(),
        email: r.email.trim() || undefined,
        amount: parseFloat(r.amount),
        reference: r.reference.trim() || undefined,
        employeeRef: r.employeeRef.trim() || undefined,
      }));

    try {
      const res = await fetch(`${apiBase}/preview-beneficiaries`, {
        method: 'POST',
        headers: {
          ...authHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ beneficiaries: payload }),
      });
      const data = await res.json();

      if (data.success) {
        applyPreviewResponse(data.data);
      } else {
        setError(data.error || 'Validation failed');
      }
    } catch {
      setError('Network error during validation');
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('rail', WALLET_RAIL);

    try {
      const res = await fetch(
        `${apiBase}/upload-beneficiaries?rail=${encodeURIComponent(WALLET_RAIL)}`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: formData,
        },
      );
      const data = await res.json();

      if (data.success) {
        applyPreviewResponse(data.data);
      } else {
        setError(data.error || 'Upload failed');
      }
    } catch {
      setError('Network error during upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    (async () => {
      try {
        const res = await fetch(`${apiBase}/template.csv`, {
          headers: authHeaders(),
        });
        if (!res.ok) {
          setError('Failed to download template');
          return;
        }
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = 'disbursement_wallet_template.csv';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(blobUrl);
      } catch {
        setError('Download failed');
      }
    })();
  };

  const handleCreateRun = async () => {
    const validBeneficiaries = beneficiaries.filter((b) => b.validation.valid);
    if (validBeneficiaries.length === 0) {
      setError('No valid beneficiaries to submit');
      return;
    }

    const batchRef = paymentBatchRef.trim().toUpperCase().replace(/\s+/g, '');
    if (isDepositPerBatch && !batchRef) {
      setError('payment_batch_reference is required — add it to your CSV or enter it below');
      return;
    }
    if (floatShortfall) {
      setError(
        `Insufficient available float: available ${formatZAR((availableCents || 0) / 100)}, ` +
        `required ${formatZAR(requiredCents / 100)}. Top up the partner float before creating this run.`,
      );
      return;
    }

    setCreating(true);
    setError('');

    try {
      const res = await fetch(`${apiBase}/runs`, {
        method: 'POST',
        headers: {
          ...authHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rail: WALLET_RAIL,
          ...(isDepositPerBatch && batchRef ? { paymentBatchReference: batchRef } : {}),
          beneficiaries: validBeneficiaries.map((b) => ({
            name: b.beneficiaryName,
            msisdn: b.msisdn,
            email: b.email || undefined,
            amount: b.amount,
            reference: b.reference || undefined,
            employeeRef: b.employeeRef || undefined,
            ...(isDepositPerBatch && batchRef ? { paymentBatchReference: batchRef } : {}),
          })),
        }),
      });

      const data = await res.json();

      if (data.success) {
        navigate(cpPath(`/runs/${data.data.run?.id || data.data.id}`));
      } else {
        setError(data.error || 'Failed to create run');
      }
    } catch {
      setError('Network error');
    } finally {
      setCreating(false);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setManualRows([{ ...EMPTY_MANUAL_ROW }]);
    setUploadMode('file');
    setBeneficiaries([]);
    setSummary(null);
    setCensus(null);
    setError('');
    setStep('upload');
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex items-center gap-2">
        {(['upload', 'preview', 'configure'] as Step[]).map((s, i) => (
          <React.Fragment key={s}>
            {i > 0 && <div className="h-px flex-1 bg-[var(--border)]" />}
            <div
              className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${
                step === s
                  ? 'border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]'
                  : beneficiaries.length > 0 && i < ['upload', 'preview', 'configure'].indexOf(step)
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : 'border-[var(--border)] text-[var(--muted-foreground)]'
              }`}
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-current/10 text-[10px]">
                {i + 1}
              </span>
              {s === 'upload' ? 'Upload' : s === 'preview' ? 'Preview' : 'Create Run'}
            </div>
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div className="rounded-lg border border-[var(--destructive)]/20 bg-[var(--destructive)]/10 px-4 py-3 text-sm text-[var(--destructive)]">
          {error}
        </div>
      )}

      {step === 'upload' && (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-6">
          <div className="mb-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => { setUploadMode('file'); setError(''); }}
              className={`flex min-h-[36px] items-center gap-2 rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${
                uploadMode === 'file'
                  ? 'bg-[var(--foreground)] text-[var(--background)]'
                  : 'bg-[var(--muted)] text-[var(--muted-foreground)]'
              }`}
            >
              <Upload className="h-4 w-4" aria-hidden />
              Upload file
            </button>
            <button
              type="button"
              onClick={() => { setUploadMode('manual'); setError(''); }}
              className={`flex min-h-[36px] items-center gap-2 rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${
                uploadMode === 'manual'
                  ? 'bg-[var(--foreground)] text-[var(--background)]'
                  : 'bg-[var(--muted)] text-[var(--muted-foreground)]'
              }`}
            >
              <PenLine className="h-4 w-4" aria-hidden />
              Manual entry
            </button>
          </div>

          <h3 className="mb-2 text-sm font-semibold text-[var(--foreground)]">
            {uploadMode === 'file' ? 'Upload MyMoolah Wallet Disbursement File' : 'Enter Beneficiaries Manually'}
          </h3>
          <p className="mb-4 text-xs text-[var(--muted-foreground)]">
            Pay beneficiaries into MyMoolah wallets only. New recipients receive an SMS (and email if provided) with a wallet invite link. Funds must be claimed within 96 hours.
          </p>

          <div className="mb-4 rounded-lg border border-[var(--border)] bg-[var(--muted)]/30 p-4 text-xs text-[var(--muted-foreground)] leading-relaxed">
            <p className="font-medium text-[var(--foreground)]">Field reference</p>
            <p className="mt-1">
              CSV, Excel, and manual entry use the <strong className="text-[var(--foreground)]">same column names</strong>.
              {uploadMode === 'manual' ? ' The table below matches your upload file headers.' : ' Use these exact headers in row 1 of your file.'}
            </p>
            {!isDepositPerBatch && (
              <p className="mt-2 rounded-md border border-emerald-200 bg-emerald-50/80 px-2 py-1.5 text-emerald-900">
                <strong>Prefunded float</strong> — payments use your partner float after checker approval (no per-run bank deposit).
                The <span className="font-mono">payment_batch_reference</span> column is not required.
              </p>
            )}
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[480px] text-left text-[11px]">
                <thead>
                  <tr className="border-b border-[var(--border)] text-[var(--muted-foreground)]">
                    <th className="py-1.5 pr-3 font-medium">Column</th>
                    <th className="py-1.5 pr-3 font-medium">Req.</th>
                    <th className="py-1.5 font-medium">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {isDepositPerBatch && (
                    <tr className="border-b border-[var(--border)]/60">
                      <td className="py-1.5 pr-3 align-top">
                        <CsvColumnLabel name={DEPOSIT_BATCH_FIELD.csvHeader} required />
                      </td>
                      <td className="py-1.5 pr-3 align-top text-red-600">Yes</td>
                      <td className="py-1.5 align-top">{DEPOSIT_BATCH_FIELD.hint}</td>
                    </tr>
                  )}
                  {WALLET_UPLOAD_FIELDS.map((field) => (
                    <tr key={field.csvHeader} className="border-b border-[var(--border)]/60 last:border-0">
                      <td className="py-1.5 pr-3 align-top">
                        <CsvColumnLabel name={field.csvHeader} required={field.required} />
                      </td>
                      <td className="py-1.5 pr-3 align-top">{field.required ? 'Yes' : 'No'}</td>
                      <td className="py-1.5 align-top">{field.hint}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {uploadMode === 'file' ? (
            <>
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-10 transition-colors ${
              isDragging
                ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                : file
                ? 'border-emerald-300 bg-emerald-50'
                : 'border-[var(--border)] hover:border-[var(--primary)]/50 hover:bg-[var(--muted)]/30'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFileSelect(f);
              }}
            />

            {file ? (
              <>
                <FileSpreadsheet className="mb-3 h-10 w-10 text-emerald-600" aria-hidden />
                <p className="text-sm font-medium text-[var(--foreground)]">{file.name}</p>
              </>
            ) : (
              <>
                <Upload className="mb-3 h-10 w-10 text-[var(--muted-foreground)]/50" aria-hidden />
                <p className="text-sm font-medium text-[var(--foreground)]">
                  Drop your CSV or Excel file here
                </p>
                <p className="mt-1 text-xs text-[var(--muted-foreground)]">Wallet disbursements only</p>
              </>
            )}
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={handleDownloadTemplate} className="flex min-h-[36px] items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--card)] px-4 py-1.5 text-sm">
              <Download className="h-4 w-4" aria-hidden />
              Download template
            </button>
            {file && (
              <button type="button" onClick={resetUpload} className="flex min-h-[36px] items-center gap-2 rounded-lg border px-4 py-1.5 text-sm text-[var(--muted-foreground)]">
                <Trash2 className="h-4 w-4" aria-hidden />
                Clear
              </button>
            )}
            <button
              type="button"
              onClick={handleUpload}
              disabled={!file || uploading}
              className="flex min-h-[36px] items-center gap-2 rounded-lg bg-[var(--primary)] px-4 py-1.5 text-sm font-medium text-[var(--primary-foreground)] disabled:opacity-60"
            >
              {uploading ? 'Parsing...' : 'Parse File'}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </button>
          </div>
            </>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border border-[var(--border)]">
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead className="bg-[var(--muted)]/30">
                    <tr className="border-b">
                      {WALLET_UPLOAD_FIELDS.map((field) => (
                        <th key={field.csvHeader} className="px-2 py-2">
                          <CsvColumnLabel name={field.csvHeader} required={field.required} />
                        </th>
                      ))}
                      <th className="px-2 py-2" aria-label="Remove row" />
                    </tr>
                  </thead>
                  <tbody>
                    {manualRows.map((row, i) => (
                      <tr key={i} className="border-b last:border-b-0">
                        {WALLET_UPLOAD_FIELDS.map((field) => (
                          <td key={field.csvHeader} className="px-1 py-1">
                            <input
                              type={field.manualKey === 'email' ? 'email' : 'text'}
                              inputMode={
                                field.manualKey === 'msisdn'
                                  ? 'tel'
                                  : field.manualKey === 'amount'
                                    ? 'decimal'
                                    : undefined
                              }
                              value={row[field.manualKey]}
                              onChange={(e) => updateManualRow(i, field.manualKey, e.target.value)}
                              className={`w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1.5 text-xs ${
                                field.mono ? 'font-mono' : ''
                              } ${field.manualKey === 'name' ? 'min-w-[120px]' : ''}`}
                              placeholder={field.placeholder}
                              aria-label={field.csvHeader}
                            />
                          </td>
                        ))}
                        <td className="px-1 py-1">
                          <button
                            type="button"
                            onClick={() => removeManualRow(i)}
                            disabled={manualRows.length <= 1}
                            className="rounded-md p-1.5 text-[var(--muted-foreground)] hover:bg-[var(--muted)] disabled:opacity-40"
                            aria-label={`Remove row ${i + 1}`}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={addManualRow}
                  className="flex min-h-[36px] items-center gap-2 rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm"
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  Add row
                </button>
                <button
                  type="button"
                  onClick={handleManualPreview}
                  disabled={uploading}
                  className="flex min-h-[36px] items-center gap-2 rounded-lg bg-[var(--primary)] px-4 py-1.5 text-sm font-medium text-[var(--primary-foreground)] disabled:opacity-60"
                >
                  {uploading ? 'Validating...' : 'Validate & Preview'}
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {step === 'preview' && (
        <>
          {summary && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-lg border p-3 text-center">
                <p className="text-xs text-[var(--muted-foreground)]">Total rows</p>
                <p className="text-lg font-bold">{summary.total}</p>
              </div>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center">
                <p className="text-xs text-emerald-600">Valid</p>
                <p className="text-lg font-bold text-emerald-700">{summary.valid}</p>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center">
                <p className="text-xs text-red-600">Invalid</p>
                <p className="text-lg font-bold text-red-700">{summary.invalid}</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-xs text-[var(--muted-foreground)]">Total amount</p>
                <p className="text-lg font-bold">{formatZAR(summary.totalAmount)}</p>
              </div>
            </div>
          )}

          {census && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
                <p className="text-xs text-emerald-700">Wallet ready</p>
                <p className="text-xl font-bold text-emerald-800">{census.registered}</p>
              </div>
              <div className="rounded-lg border border-sky-200 bg-sky-50/50 p-3">
                <p className="text-xs text-sky-700">Invites required</p>
                <p className="text-xl font-bold text-sky-800">{census.invite_required}</p>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                <p className="text-xs text-amber-700">Probable typo</p>
                <p className="text-xl font-bold text-amber-800">{census.probable_typo}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-[var(--muted-foreground)]">Invalid</p>
                <p className="text-xl font-bold">{census.invalid}</p>
              </div>
            </div>
          )}

          <div className="overflow-hidden rounded-xl border bg-[var(--card)]">
            <div className="max-h-[400px] overflow-auto">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-[var(--card)]">
                  <tr className="border-b bg-[var(--muted)]/30">
                    <th className="px-4 py-2.5">#</th>
                    <th className="px-4 py-2.5">Valid</th>
                    <th className="px-4 py-2.5">Outcome</th>
                    <th className="px-4 py-2.5">recipient_name</th>
                    <th className="px-4 py-2.5">msisdn</th>
                    <th className="px-4 py-2.5">email</th>
                    <th className="px-4 py-2.5 text-right">amount_zar</th>
                    <th className="px-4 py-2.5">Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {beneficiaries.map((b, i) => (
                    <tr key={i} className={`border-b ${!b.validation.valid ? 'bg-red-50/30' : ''}`}>
                      <td className="px-4 py-2.5 text-xs">{i + 1}</td>
                      <td className="px-4 py-2.5">
                        {b.validation.valid ? (
                          <CheckCircle className="h-4 w-4 text-emerald-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-500" />
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-xs font-medium">
                        {BUCKET_LABELS[b.census?.bucket || 'invalid'] || b.census?.bucket}
                        {b.census?.typo?.suggestedMatch && (
                          <span className="mt-0.5 block text-[10px] font-normal text-amber-600">
                            Suggested: {b.census.typo.suggestedMatch}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">{b.beneficiaryName || '-'}</td>
                      <td className="px-4 py-2.5 font-mono text-xs">{b.msisdn || '-'}</td>
                      <td className="px-4 py-2.5 text-xs">{b.email || '-'}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{b.amount ? formatZAR(b.amount) : '-'}</td>
                      <td className="px-4 py-2.5 text-[11px] text-red-600">
                        {b.validation.errors[0]}
                        {b.validation.warnings[0] && (
                          <span className="flex items-center gap-1 text-amber-600">
                            <AlertTriangle className="h-3 w-3" />
                            {b.validation.warnings[0]}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={resetUpload} className="rounded-lg border px-4 py-1.5 text-sm">
              Start Over
            </button>
            <button
              type="button"
              onClick={() => setStep('configure')}
              disabled={!summary || summary.valid === 0}
              className="rounded-lg bg-[var(--primary)] px-4 py-1.5 text-sm font-medium text-[var(--primary-foreground)] disabled:opacity-60"
            >
              Continue
            </button>
          </div>
        </>
      )}

      {step === 'configure' && (
        <div className="rounded-xl border bg-[var(--card)] p-6">
          <h3 className="mb-4 text-sm font-semibold">Create Wallet Disbursement Run</h3>

          {summary && (
            <div className="rounded-lg border bg-[var(--muted)]/30 p-4 text-sm">
              <p>
                <span className="font-medium">{summary.valid}</span> beneficiaries totalling{' '}
                <span className="font-medium">{formatZAR(summary.totalAmount)}</span>
              </p>
              {!isDepositPerBatch && (
                <p className="mt-2 text-xs text-[var(--muted-foreground)]">
                  This run will use your prefunded float
                  {availableCents != null ? ` (available ${formatZAR(availableCents / 100)})` : ''}.
                  A checker must approve before payments are sent.
                </p>
              )}
              {floatShortfall && (
                <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden />
                  <p>
                    Insufficient available float. Available {formatZAR((availableCents || 0) / 100)},
                    required {formatZAR(requiredCents / 100)}. Top up the partner float before creating this run.
                  </p>
                </div>
              )}
              {isDepositPerBatch && (
              <div className="mt-4">
                <label className="mb-1 block text-xs font-medium text-[var(--foreground)]">
                  Payment batch reference (bank deposit)
                </label>
                <input
                  type="text"
                  value={paymentBatchRef}
                  onChange={(e) => setPaymentBatchRef(e.target.value)}
                  placeholder="e.g. PAY-WEPLEDGE-20260701-001"
                  className="w-full rounded-lg border bg-[var(--background)] px-3 py-2 font-mono text-sm"
                />
                <p className="mt-2 text-xs text-[var(--muted-foreground)]">
                  Quote this exact reference on your bulk EFT to MyMoolah. Treasury matches it to this run
                  and allocates wallet credits from that one deposit.
                </p>
              </div>
              )}
            </div>
          )}

          {census && census.probable_typo > 0 && summary && (
            <p className="mt-2 text-xs text-amber-600">
              {census.probable_typo} row(s) flagged as probable typo — support will verify before invites are sent.
            </p>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={() => setStep('preview')} className="rounded-lg border px-4 py-1.5 text-sm">
              Back
            </button>
            <button
              type="button"
              onClick={handleCreateRun}
              disabled={creating || floatShortfall}
              className="rounded-lg bg-[var(--primary)] px-4 py-1.5 text-sm font-medium text-[var(--primary-foreground)] disabled:opacity-60"
            >
              {creating ? 'Creating...' : 'Create Run'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
